import React, { createContext, useContext, useState } from 'react';
import { disconnectSocket } from '../lib/socket';
import { removeFCMToken } from '../api/notifications';

const AuthContext = createContext(null);

const getStoredUser = () => {
  try {
    const token = localStorage.getItem('token');
    const raw   = localStorage.getItem('user');
    if (token && raw) return JSON.parse(raw);
  } catch {}
  return null;
};

export const AuthProvider = ({ children }) => {
  // screen: 'login' | 'register' | 'otp' | 'add-property' | 'pending' | 'app'
  const [screen,      setScreen]      = useState(() => getStoredUser() ? 'app' : 'login');
  const [user,        setUser]        = useState(() => getStoredUser());
  const [pendingData, setPendingData] = useState(null);
  const [setupToken,  setSetupToken]  = useState(() => sessionStorage.getItem('setup_token') || null);

  // Called by useLogin on success
  const onLoginSuccess = (userData) => {
    // Check active status — inactive users must not reach the app
    const isActive =
      userData?.is_active ??
      userData?.active ??
      (userData?.status != null ? userData.status !== 'inactive' : undefined) ??
      true;

    if (!isActive) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setScreen('login');
      return;
    }

    // Check OTP/email verification
    const otpVerified =
      userData?.is_otp_verified ??
      userData?.is_email_verified ??
      userData?.email_verified ??
      userData?.otp_verified ??
      userData?.is_verified ??
      true;

    if (!otpVerified) {
      setPendingData({ email: userData.email });
      setScreen('otp');
      return;
    }

    // Check admin approval
    const adminApproved =
      userData?.is_approved ??
      userData?.is_admin_approved ??
      userData?.admin_approved ??
      (userData?.status ? userData.status === 'approved' : undefined) ??
      true;

    if (!adminApproved) {
      setPendingData({ email: userData.email });
      setScreen('pending');
      return;
    }

    setUser(userData);
    setScreen('app');
  };

  // Called by useLogin when API rejects with OTP-not-verified error
  const onLoginOtpRequired = (email) => {
    setPendingData({ email });
    setScreen('otp');
  };

  // Called by useLogin when API rejects with pending-approval error
  const onLoginPendingApproval = (email) => {
    setPendingData({ email });
    setScreen('pending');
  };

  // Called by useRegister on success — save setup_token and move to add-property screen
  const onRegisterSuccess = (data) => {
    setPendingData({ email: data?.email });
    if (data?.setup_token) {
      setSetupToken(data.setup_token);
      sessionStorage.setItem('setup_token', data.setup_token);
    }
    setScreen('add-property');
  };

  // Called by useVerifyOtp on success — clear setup_token and show pending approval
  const onOtpSuccess = () => {
    setSetupToken(null);
    sessionStorage.removeItem('setup_token');
    setScreen('pending');
  };

  // Called by useSetupProperty on success — move to OTP screen
  const onPropertyAdded = () => {
    setScreen('otp');
  };

  const logout = () => {
    disconnectSocket();
    const fcmToken = localStorage.getItem('fcm_token');
    if (fcmToken) {
      removeFCMToken(fcmToken).catch(() => {});
      localStorage.removeItem('fcm_token');
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPendingData(null);
    setScreen('login');
  };

  const goTo = (s) => setScreen(s);

  return (
    <AuthContext.Provider value={{
      screen,
      user,
      pendingData,
      setupToken,
      onLoginSuccess,
      onLoginOtpRequired,
      onLoginPendingApproval,
      onRegisterSuccess,
      onOtpSuccess,
      onPropertyAdded,
      logout,
      goTo,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
